// نگاشت خطای problem+json سرور به فیلدهای React Hook Form (AP-PLT-02؛ C-AGENT-201).
// سرور خطای فیلد را با کلید پیام (مثل validation.tenant_slug_pattern) در `errors` و خطای دامنه را با `code` برمی‌گرداند.
import { errorMessageKey } from '@phoenix/i18n';
import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';

/** شکل حداقلی ApiError (@phoenix/api-client) برای نگاشت فیلدی؛ ui به api-client وابسته نیست. */
export interface FieldErrorSource {
  code?: string;
  status?: number;
  fieldErrors?: Readonly<Record<string, readonly string[]>>;
}

export interface ServerErrorMapping<T extends FieldValues> {
  /** فیلدهای فرم (برای تطبیق نام فیلد سرور بدون حساسیت به بزرگی حرف و `_`). */
  fields: readonly FieldPath<T>[];
  /** نام‌های متفاوت سرور ← فیلد فرم (مثل `province_code` ← `provinceCode`). */
  aliases?: Readonly<Record<string, FieldPath<T>>>;
  /** کد خطای دامنه ← فیلد (مثل `PHX-PLT-0101` ← `slug`)؛ پیام همان پیام ترجمه‌شده کد است. */
  codeFields?: Readonly<Record<string, FieldPath<T>>>;
}

function asSource(error: unknown): FieldErrorSource | undefined {
  return error && typeof error === 'object' ? (error as FieldErrorSource) : undefined;
}

function canonical(name: string): string {
  return name.replace(/[_\-.]/g, '').toLowerCase();
}

/** کلید پیام سرور ← کلید i18n کلاینت (`validation.x` ← `errors:validation.x`). */
export function serverMessageKey(message: string): string {
  if (message.includes(':')) {
    return message;
  }
  return /^(validation|error)\./.test(message) ? `errors:${message}` : 'errors:validation.invalid';
}

/**
 * خطاهای فیلدی و کدهای نگاشت‌شده را روی فرم می‌نشاند و تعداد فیلدهای علامت‌خورده را برمی‌گرداند؛ صفر یعنی خطا فیلدی نیست
 * و فراخوان باید آن را به‌صورت عمومی (ErrorState) نشان دهد.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  mapping: ServerErrorMapping<T>,
): number {
  const source = asSource(error);
  if (!source) {
    return 0;
  }
  const byCanonical = new Map<string, FieldPath<T>>(mapping.fields.map((field) => [canonical(field), field]));
  let applied = 0;
  let focused = false;

  for (const [serverName, messages] of Object.entries(source.fieldErrors ?? {})) {
    const field = mapping.aliases?.[serverName] ?? byCanonical.get(canonical(serverName));
    const message = messages[0];
    if (!field || !message) {
      continue;
    }
    setError(field, { type: 'server', message: serverMessageKey(message) }, { shouldFocus: !focused });
    focused = true;
    applied += 1;
  }

  const codeField = source.code ? mapping.codeFields?.[source.code] : undefined;
  if (codeField) {
    setError(
      codeField,
      { type: 'server', message: errorMessageKey(source.code, source.status) },
      { shouldFocus: !focused },
    );
    applied += 1;
  }
  return applied;
}
